import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import type { IncidentPeriodSummary, IncidentSummaryResponse } from "@visioni/a3s-monitoring/app";

type Counts = IncidentSummaryResponse["totals"];

/**
 * The headline figures, in the order support reads them: what operators acted
 * on, then the activations, so they compare with one another ("15 activations,
 * 10 in 3 genuine incidents, 5 non-genuine").
 */
const HEADLINES = [
  { key: "genuineIncidents", label: "Genuine incidents" },
  { key: "policeIncidents", label: "Police incidents" },
  { key: "activationsInGenuineIncidents", label: "Activations in genuine incidents" },
  { key: "nonGenuineActivations", label: "Non-genuine activations" },
  { key: "technicalActivations", label: "Technical activations" },
] as const satisfies readonly { key: keyof Counts; label: string }[];

const PROVISIONAL =
  "Provisional: some incidents or activations counted here have not settled, so these figures may still change.";

/**
 * Headline totals for the chosen window, then the same counts per day, week or
 * month. A period with nothing counted is absent rather than zero.
 */
export function IncidentTotals({
  summary,
  isStale,
}: {
  readonly summary: IncidentSummaryResponse;
  readonly isStale: boolean;
}) {
  const { totals, periods } = summary;
  return (
    <View style={[styles.wrapper, isStale && styles.stale]}>
      <View style={styles.tiles}>
        {HEADLINES.map(({ key, label }) => (
          <View key={key} style={styles.tile}>
            <Text textStyle="xs/semibold" color="textMuted">
              {label}
            </Text>
            <Text textStyle="lg/bold" color="text">
              {totals[key].toLocaleString("en-AU")}
            </Text>
          </View>
        ))}
      </View>
      {!totals.settled && (
        <Text textStyle="xs/regular" color="textMuted">
          {PROVISIONAL}
        </Text>
      )}
      {periods.length > 0 && <PeriodTable periods={periods} />}
    </View>
  );
}

function PeriodTable({ periods }: { readonly periods: readonly IncidentPeriodSummary[] }) {
  return (
    <View style={styles.card}>
      <View style={[styles.row, styles.headerRow]}>
        <Text textStyle="xs/semibold" color="textMuted" style={styles.period}>
          Period
        </Text>
        {HEADLINES.map(({ key, label }) => (
          <Text key={key} textStyle="xs/semibold" color="textMuted" style={styles.count}>
            {label}
          </Text>
        ))}
      </View>
      {periods.map((period) => (
        <View key={period.period} style={styles.row}>
          <Text textStyle="xs/regular" color="text" style={styles.period}>
            {period.settled ? period.period : `${period.period} (provisional)`}
          </Text>
          {HEADLINES.map(({ key }) => (
            <Text key={key} textStyle="xs/regular" color="text" style={styles.count}>
              {period[key].toLocaleString("en-AU")}
            </Text>
          ))}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  wrapper: { gap: theme.spacing.lg },
  stale: { opacity: 0.5 },
  tiles: { flexDirection: "row", flexWrap: "wrap", gap: theme.spacing.lg },
  tile: {
    flexBasis: "18%",
    flexGrow: 1,
    minWidth: 160,
    gap: theme.spacing.xs,
    padding: theme.spacing.xl,
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
  },
  card: {
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
    overflow: "hidden",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.md,
    borderTopWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  headerRow: { borderTopWidth: 0, backgroundColor: theme.colors.surfaceCardInner },
  period: { width: 180 },
  count: { flex: 1, minWidth: 100, textAlign: "right" },
}));
